/*
 * variant-selects.js
 * ---------------------------------------------------------------------------
 * Local replacement for the two variant-picker custom elements that used to be
 * supplied by the remote the vendor bundle:
 *
 *   <variant-selects>       snippets/product-variant-picker.liquid:21-129
 *   <card-variant-selects>  snippets/card-product.liquid:276-350
 *
 * <variant-selects> is a port of Dawn's (MIT) VariantSelects component --
 * the pre-ProductInfo-refactor vintage, confirmed by assets/quick-add.js:74-79
 * (sets data-update-url on `variant-radios,variant-selects`) and
 * assets/quick-add.js:110-112 (stamps data-original-section on it).
 * It is adapted to this theme's actual DOM:
 *   - option wrappers are plain `div.product-form__input`, never <fieldset>
 *   - availability is expressed with the `.non-existent` / `.unavailable` /
 *     `.disabled` classes that snippets/product-variant-options.liquid:81-88,
 *     :149, :213, :225, :246 renders, NOT with Dawn's innerText swap
 *   - media-gallery.setActiveMedia() takes 4 args here (assets/media-gallery.js:29)
 *   - the add-to-cart label node is `span.main-atc__label__text`
 *     (snippets/buy-buttons.liquid:133) because the FIRST span inside
 *     [name="add"] is `span.main-atc__error` (snippets/buy-buttons.liquid:126)
 *
 * <card-variant-selects> has no Dawn upstream; it is derived entirely from
 * snippets/card-product.liquid.
 *
 * Helpers deliberately NOT defined here (they belong to the shared core file
 * that also has to perform the <html class="no-js"> -> "js" swap that
 * layout/theme.liquid:2 depends on): publish / subscribe / PUB_SUB_EVENTS,
 * debounce, trapFocus, removeTrapFocus, pauseAllMedia, fetchConfig.
 * This file only *consumes* them, always behind a typeof guard.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Small local helpers. Namespaced so they cannot collide with the
   * globals that assets/secondary.js consumes (debounce, trapFocus, ...).
   * ------------------------------------------------------------------ */

  /** Run fn, never let a failure escape into the caller (e.g. add-to-cart). */
  function vsGuard(label, fn) {
    try {
      return fn();
    } catch (error) {
      if (window.console && console.error) {
        console.error('[variant-selects] ' + label, error);
      }
      return undefined;
    }
  }

  /** window.variantStrings is defined in layout/theme.liquid:686-691. */
  function vsString(key, fallback) {
    var strings = window.variantStrings;
    if (strings && typeof strings[key] === 'string' && strings[key] !== '') {
      return strings[key];
    }
    return fallback;
  }

  /**
   * "<value> - Unavailable" text used by dropdown options.
   * Liquid equivalent: snippets/product-variant-options.liquid:230-234 which
   * renders `products.product.value_unavailable`; the same locale key is
   * exposed as window.variantStrings.unavailable_with_option
   * (layout/theme.liquid:690) and carries the `[value]` token.
   */
  function vsUnavailableWithOption(value) {
    var template = vsString('unavailable_with_option', '[value] - Unavailable');
    return template.split('[value]').join(value);
  }

  /**
   * Publish the variant change so the sibling the previous vendor-replacement elements
   * (secondary-variant-select, quantity-breaks, sticky-atc, product-info, ...)
   * can react without this element reaching into their DOM.
   *
   * Nothing in the surviving theme defines publish/subscribe, so we:
   *   1. call window.publish() when the core cluster has provided it, using
   *      Dawn's PUB_SUB_EVENTS.variantChange name ('variant-change');
   *   2. always also dispatch a bubbling DOM CustomEvent under the same name
   *      so a subscriber can be written either way.
   */
  function vsPublishVariantChange(element, payload) {
    var eventName =
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variant-change';

    vsGuard('publish', function () {
      if (typeof window.publish === 'function') {
        window.publish(eventName, { data: payload });
      }
    });

    vsGuard('dispatch ' + eventName, function () {
      element.dispatchEvent(
        new CustomEvent(eventName, { bubbles: true, detail: { data: payload } })
      );
    });
  }

  /**
   * Append a width param to a Shopify CDN image url.
   * variant.featured_image.src already carries `?v=...`, so we must use `&`.
   */
  function vsImageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ------------------------------------------------------------------ *
   * Shared base class. NOT registered as a custom element -- it only
   * carries the option-reading / variant-matching logic that both
   * <variant-selects> and <card-variant-selects> need.
   * ------------------------------------------------------------------ */

  var BaseElement = class VariantPickerBaseElement extends HTMLElement {
    connectedCallback() {
      // Idempotent: assets/facets.js re-renders collection grids and
      // assets/quick-add.js injects a cloned copy of the PDP markup, so an
      // element can be connected more than once.
      if (this.variantPickerBound) return;
      this.variantPickerBound = true;

      this.boundOnVariantChange = this.onVariantChange.bind(this);
      this.addEventListener('change', this.boundOnVariantChange);
    }

    disconnectedCallback() {
      if (!this.variantPickerBound) return;
      this.removeEventListener('change', this.boundOnVariantChange);
      this.variantPickerBound = false;
    }

    /**
     * The `<script type="application/json">` is the LAST child of the element:
     * snippets/product-variant-picker.liquid:126-128 and
     * snippets/card-product.liquid:347-349. It holds `product.variants | json`.
     */
    getVariantData() {
      if (this.variantData) return this.variantData;

      this.variantData = [];
      for (var i = this.children.length - 1; i >= 0; i--) {
        var child = this.children[i];
        if (child.tagName === 'SCRIPT' && child.getAttribute('type') === 'application/json') {
          this.variantData = vsGuard('parse variant JSON', function () {
            return JSON.parse(child.textContent);
          }) || [];
          break;
        }
      }
      if (!Array.isArray(this.variantData)) this.variantData = [];
      return this.variantData;
    }

    /**
     * One wrapper per entry of product.options_with_values, IN OPTION ORDER.
     * They are DIRECT children of the element:
     *   snippets/product-variant-picker.liquid:48-51
     *   snippets/card-product.liquid:290-292
     * Reading direct children only is important: `#Quantity-Form-<section>`
     * (sections/main-product.liquid:355-356) also carries the class
     * `product-form__input` but lives outside this element, and
     * snippets/sizing-chart.liquid:98-101 appends a modal INTO the second
     * wrapper's `.form__label` which we must never disturb.
     */
    getOptionWrappers() {
      var wrappers = [];
      var i;

      for (i = 0; i < this.children.length; i++) {
        var child = this.children[i];
        if (child.classList && child.classList.contains('product-form__input')) {
          wrappers.push(child);
        }
      }
      if (wrappers.length) return wrappers;

      // Defensive fallback if a future markup change nests the wrappers.
      var tag = this.tagName.toLowerCase();
      var nested = this.querySelectorAll('.product-form__input');
      for (i = 0; i < nested.length; i++) {
        if (nested[i].closest(tag) === this) wrappers.push(nested[i]);
      }
      return wrappers;
    }

    /**
     * Read the selected value of every option wrapper, in DOM order.
     * Dawn's `querySelectorAll('select, fieldset')` cannot be used: this theme
     * renders NO <fieldset>. Per wrapper we use the <select> when the picker
     * type is `dropdown` (snippets/product-variant-picker.liquid:82-87) and the
     * checked radio otherwise (snippets/product-variant-options.liquid:139,
     * :204, :237 and snippets/card-product.liquid:316).
     *
     * Every lookup is scoped to `this`: the radio ids
     * `{{ section.id }}-{{ option.position }}-{{ forloop.index0 }}` are NOT
     * unique once the same snippet is reused inside a collection card.
     *
     * @returns {Array<string|null>} null for a wrapper with nothing selected.
     */
    updateOptions() {
      var values = [];

      this.getOptionWrappers().forEach(function (wrapper) {
        var select = wrapper.querySelector('select.select__select');

        if (select) {
          // The sticky bar's COMBINED picker (sections/main-product.liquid:533)
          // is a single select whose value is every option joined by a comma:
          // `<option value='{{ variant.options | join: "," }}' data-options='...'>`.
          // Read it back as N option values, not one. Without this split a
          // multi-option product never matches a variant, which clears the
          // variant input and disables Add to Cart.
          var chosen = select.options[select.selectedIndex];
          var combined = chosen && chosen.getAttribute('data-options');

          if (combined !== null && typeof combined !== 'undefined') {
            values = values.concat(combined.split(','));
          } else {
            values.push(select.value);
          }
          return;
        }

        var checked = wrapper.querySelector('input[type="radio"]:checked');
        values.push(checked ? checked.value : null);
      });

      this.options = values;
      return this.options;
    }

    /** True when every option wrapper resolved to a value. */
    hasCompleteSelection() {
      if (!this.options || !this.options.length) return false;
      for (var i = 0; i < this.options.length; i++) {
        if (this.options[i] === null || typeof this.options[i] === 'undefined') return false;
      }
      return true;
    }

    /** Dawn upstream, unchanged: positional match of variant.options. */
    updateMasterId() {
      var options = this.options;
      this.currentVariant = this.getVariantData().find(function (variant) {
        return !variant.options
          .map(function (option, index) {
            return options[index] === option;
          })
          .includes(false);
      });
      return this.currentVariant;
    }

    onVariantChange() {
      /* implemented by the subclasses */
    }
  };

  /* ================================================================== *
   * <variant-selects>  --  the product-page picker
   * ================================================================== */

  var VariantSelects = class VariantSelects extends BaseElement {
    onVariantChange() {
      var self = this;

      vsGuard('onVariantChange', function () {
        self.updateOptions();

        // A wrapper with nothing selected means we cannot resolve a variant at
        // all. Bail out rather than clearing the form and killing add-to-cart.
        if (!self.hasCompleteSelection()) return;

        self.updateMasterId();
        self.updateVariantStatuses();

        if (!self.currentVariant) {
          self.clearVariantInput();
          self.toggleAddButton(true, '', true);
          self.setUnavailable();
          return;
        }

        self.updateURL();
        self.updateVariantInput();
        self.updateMedia();
        self.updatePickupAvailability();
        self.renderProductInfo();
      });
    }

    /* ---------------------------------------------------------------- *
     * Availability recomputation
     * ---------------------------------------------------------------- */

    /**
     * Mirrors the Liquid at snippets/product-variant-options.liquid:46-88:
     * for the option at `index`, a value "exists" when some variant matches
     * every PRECEDING selected option value and carries that value at
     * `index`; it is "available" when such a variant has available == true.
     */
    computeValueState(index, value) {
      var variants = this.getVariantData();
      var selected = this.options;
      var exists = false;
      var available = false;

      for (var v = 0; v < variants.length; v++) {
        var variant = variants[v];
        var matchesPreceding = true;

        for (var p = 0; p < index; p++) {
          if (variant.options[p] !== selected[p]) {
            matchesPreceding = false;
            break;
          }
        }
        if (!matchesPreceding) continue;
        if (variant.options[index] !== value) continue;

        exists = true;
        if (variant.available) {
          available = true;
          break;
        }
      }

      return { exists: exists, available: available };
    }

    /**
     * The theme's replacement for Dawn's setInputAvailability(): we only move
     * classes, we never rewrite pill label text (the markup already carries a
     * pre-rendered `span.visually-hidden` at
     * snippets/product-variant-options.liquid:217 which base.css reveals via
     * `input[type="radio"]:not(.disabled) + label > .visually-hidden`).
     *
     * Hiding is done purely in CSS, driven by the element's own
     * data-skip-non-existent (always "true",
     * snippets/product-variant-picker.liquid:26) and data-skip-unavailable
     * (snippets/product-variant-picker.liquid:27).
     */
    updateVariantStatuses() {
      var self = this;
      var skipUnavailable = this.dataset.skipUnavailable === 'true';

      this.getOptionWrappers().forEach(function (wrapper, index) {
        // Dropdown: snippets/product-variant-picker.liquid:81-87 ->
        // snippets/product-variant-options.liquid:220-235
        var dropdownOptions = wrapper.querySelectorAll('select.select__select option');
        if (dropdownOptions.length) {
          for (var i = 0; i < dropdownOptions.length; i++) {
            var optionEl = dropdownOptions[i];
            var state = self.computeValueState(index, optionEl.value);

            optionEl.classList.toggle('non-existent', !state.exists);
            optionEl.classList.toggle('unavailable', state.exists && !state.available);

            // Liquid emits `disabled hidden` only when it also hides the value.
            var hide = !state.exists || (!state.available && skipUnavailable);
            optionEl.disabled = hide;
            if (hide) {
              optionEl.setAttribute('hidden', '');
            } else {
              optionEl.removeAttribute('hidden');
            }

            optionEl.textContent = state.available
              ? optionEl.value
              : vsUnavailableWithOption(optionEl.value);
          }
          return;
        }

        // Swatches / pills / quantity-breaks / hidden -> radio inputs.
        // snippets/product-variant-options.liquid:139-150, :204-214, :237-247
        var inputs = wrapper.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < inputs.length; j++) {
          var input = inputs[j];
          var inputState = self.computeValueState(index, input.value);

          input.classList.toggle('non-existent', !inputState.exists);
          input.classList.toggle('unavailable', inputState.exists && !inputState.available);
          input.classList.toggle('disabled', !inputState.available);

          // The swatch wrapper also carries the class:
          // `<div class="color-swatch {{ option_class }}">` (:138). It is what
          // the base.css `[data-skip-non-existent="true"] .non-existent` rule
          // hides, so it must be kept in sync with the input.
          var swatch = input.parentElement;
          if (swatch && swatch.classList && swatch.classList.contains('color-swatch')) {
            swatch.classList.toggle('non-existent', !inputState.exists);
            swatch.classList.toggle('unavailable', inputState.exists && !inputState.available);
          }
        }
      });
    }

    /* ---------------------------------------------------------------- *
     * URL / hidden input
     * ---------------------------------------------------------------- */

    /**
     * data-update-url is ABSENT on the PDP (main-product.liquid:722-729 never
     * passes update_url), so the default is TRUE. assets/quick-add.js:78 sets
     * it to "false" on the modal copy.
     */
    updateURL() {
      if (!this.currentVariant) return;
      if (this.dataset.updateUrl === 'false') return;

      window.history.replaceState({}, '', this.dataset.url + '?variant=' + this.currentVariant.id);
    }

    /** The <form> produced by snippets/buy-buttons.liquid:80-86. */
    getProductForm() {
      return document.getElementById('product-form-' + this.dataset.section);
    }

    updateVariantInput() {
      var form = this.getProductForm();
      if (!form) return;

      // snippets/buy-buttons.liquid:88-96 (also carries .product-variant-id)
      var input = form.querySelector('input[name="id"]');
      if (!input) return;

      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clearVariantInput() {
      var form = this.getProductForm();
      if (!form) return;

      var input = form.querySelector('input[name="id"]');
      if (!input) return;

      input.value = '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* ---------------------------------------------------------------- *
     * Media
     * ---------------------------------------------------------------- */

    updateMedia() {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;

      var sectionId = this.dataset.section;
      var currentVariant = this.currentVariant;

      // snippets/product-media-gallery.liquid:86 renders
      // id="MediaGallery-{{ section.id }}{{ id_append }}" where id_append is
      // "-duplicate" for the second copy, hence the prefix selector.
      var galleries = document.querySelectorAll('[id^="MediaGallery-' + sectionId + '"]');
      var filtering = this.dataset.hasFiltering === 'true';

      for (var i = 0; i < galleries.length; i++) {
        (function (gallery) {
          vsGuard('setActiveMedia', function () {
            if (typeof gallery.setActiveMedia !== 'function') return;
            // 4-arg signature is required here (assets/media-gallery.js:29);
            // it differs from stock Dawn's 2-arg version.
            gallery.setActiveMedia(
              sectionId + '-' + currentVariant.featured_media.id,
              true,
              filtering,
              currentVariant
            );
          });
        })(galleries[i]);
      }

      // snippets/product-media-modal.liquid:12 and :30. Inside the modal the
      // slides use the RAW media id (snippets/product-media.liquid:35), unlike
      // the gallery which prefixes it with the section id.
      vsGuard('product modal prepend', function () {
        var modalContent = document.querySelector(
          '#ProductModal-' + sectionId + ' .product-media-modal__content'
        );
        if (!modalContent) return;

        var newMedia = modalContent.querySelector(
          '[data-media-id="' + currentVariant.featured_media.id + '"]'
        );
        if (newMedia) modalContent.prepend(newMedia);
      });
    }

    /* ---------------------------------------------------------------- *
     * Pickup availability -- snippets/buy-buttons.liquid:217-237
     * ---------------------------------------------------------------- */

    updatePickupAvailability() {
      var pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.dataset.variantId = this.currentVariant.id;
        if (typeof pickUpAvailability.fetchAvailability === 'function') {
          // assets/pickup-availability.js:13
          pickUpAvailability.fetchAvailability(this.currentVariant.id);
        }
      } else {
        pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    }

    /* ---------------------------------------------------------------- *
     * Section Rendering API
     * ---------------------------------------------------------------- */

    renderProductInfo() {
      var self = this;
      var requestedVariantId = this.currentVariant.id;

      // assets/quick-add.js:110-112 stamps data-original-section on the cloned
      // copy: the FETCH must use the original section id while every DOM lookup
      // must use the rewritten one (assets/quick-add.js:109).
      var sourceSectionId = this.dataset.originalSection || this.dataset.section;

      var url =
        this.dataset.url + '?variant=' + requestedVariantId + '&section_id=' + sourceSectionId;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (responseText) {
          // Guard against a stale response overwriting a newer selection.
          if (!self.currentVariant || self.currentVariant.id !== requestedVariantId) return;

          var html = new DOMParser().parseFromString(responseText, 'text/html');

          vsGuard('render price', function () {
            self.replaceNode(html, 'price-' + sourceSectionId, 'price-' + self.dataset.section, {
              removeHiddenOnDestination: true
            });
          });

          vsGuard('render inventory', function () {
            self.replaceNode(
              html,
              'Inventory-' + sourceSectionId,
              'Inventory-' + self.dataset.section,
              { hiddenWhenEmpty: true }
            );
          });

          vsGuard('render sku', function () {
            // sections/main-product.liquid:297 toggles `visibility-hidden`,
            // NOT `hidden`, for the SKU block.
            self.replaceNode(html, 'Sku-' + sourceSectionId, 'Sku-' + self.dataset.section, {
              visibilityHiddenWhenEmpty: true
            });
          });

          vsGuard('render in-button price', function () {
            self.updateInButtonPrice(html, sourceSectionId);
          });

          vsGuard('quantity rules', function () {
            self.updateQuantityRules(html, sourceSectionId);
          });

          vsGuard('toggle add button', function () {
            self.toggleAddButton(
              !self.currentVariant.available,
              vsString('soldOut', 'Sold out')
            );
          });

          vsPublishVariantChange(self, {
            sectionId: self.dataset.section,
            originalSectionId: sourceSectionId,
            html: html,
            variant: self.currentVariant
          });
        })
        .catch(function (error) {
          if (window.console && console.error) {
            console.error('[variant-selects] renderProductInfo', error);
          }
        });
    }

    /**
     * Copy the innerHTML of `sourceId` in the fetched document onto
     * `destinationId` in the live document.
     */
    replaceNode(html, sourceId, destinationId, options) {
      options = options || {};

      var destination = document.getElementById(destinationId);
      var source = html.getElementById(sourceId);
      if (!destination || !source) return;

      destination.innerHTML = source.innerHTML;

      if (options.removeHiddenOnDestination) {
        destination.classList.remove('hidden');
      }
      if (options.hiddenWhenEmpty) {
        destination.classList.toggle('hidden', source.innerText.trim() === '');
      }
      if (options.visibilityHiddenWhenEmpty) {
        destination.classList.toggle('visibility-hidden', source.innerText.trim() === '');
      }
    }

    /**
     * Optional price rendered inside the add-to-cart button:
     * snippets/buy-buttons.liquid:129-131 and :145-147. Its `hidden` class is
     * variant-dependent, so both the markup and the class come from the
     * freshly rendered section.
     */
    updateInButtonPrice(html, sourceSectionId) {
      var destination = document.getElementById('main-atc-price-' + this.dataset.section);
      var source = html.getElementById('main-atc-price-' + sourceSectionId);
      if (!destination || !source) return;

      destination.innerHTML = source.innerHTML;
      destination.classList.toggle('hidden', source.classList.contains('hidden'));
    }

    /**
     * sections/main-product.liquid:355 `#Quantity-Form-{{ section.id }}`,
     * containing `.quantity__rules` (:413), `.quantity__rules-cart` (:366) and
     * `.quantity__input` (:390-405).
     */
    updateQuantityRules(html, sourceSectionId) {
      var liveForm = document.getElementById('Quantity-Form-' + this.dataset.section);
      var updatedForm = html.getElementById('Quantity-Form-' + sourceSectionId);
      if (!liveForm || !updatedForm) return;

      var selectors = ['.quantity__input', '.quantity__rules', '.quantity__rules-cart'];

      for (var s = 0; s < selectors.length; s++) {
        var selector = selectors[s];
        var current = liveForm.querySelector(selector);
        var updated = updatedForm.querySelector(selector);
        if (!current || !updated) continue;

        if (selector === '.quantity__input') {
          // Deliberately NOT copying `value`: that would discard the shopper's
          // chosen quantity (same choice Dawn makes).
          var attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (var a = 0; a < attributes.length; a++) {
            var name = attributes[a];
            var value = updated.getAttribute(name);
            if (value !== null) {
              current.setAttribute(name, value);
            } else {
              current.removeAttribute(name);
            }
          }
        } else {
          current.innerHTML = updated.innerHTML;
          if (selector === '.quantity__rules-cart') {
            // `hidden` here is per-variant (cart_qty), main-product.liquid:366.
            current.classList.toggle('hidden', updated.classList.contains('hidden'));
          }
        }
      }

      // <quantity-input data-section="..."> (sections/main-product.liquid:383)
      // lives in another cluster; nudge it only if it exposes Dawn's method.
      var quantityInput = liveForm.querySelector('quantity-input');
      if (quantityInput && typeof quantityInput.validateQtyRules === 'function') {
        vsGuard('validateQtyRules', function () {
          quantityInput.validateQtyRules();
        });
      }
    }

    /* ---------------------------------------------------------------- *
     * Add-to-cart button
     * ---------------------------------------------------------------- */

    /**
     * @param {boolean} disable
     * @param {string}  text          label to write, '' keeps the current one
     * @param {boolean} modifyClass   unused flag kept for Dawn parity
     */
    toggleAddButton(disable, text, modifyClass) {
      var form = this.getProductForm();
      if (!form) return;

      var addButton = form.querySelector('[name="add"]');
      if (!addButton) return;

      // NOT `[name="add"] > span`: the first span is `span.main-atc__error`
      // (snippets/buy-buttons.liquid:126).
      var addButtonText = addButton.querySelector('.main-atc__label__text');

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        addButton.setAttribute('data-unavailable', 'true');
        if (addButtonText && text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButton.removeAttribute('data-unavailable');
        if (addButtonText) {
          // snippets/buy-buttons.liquid:122-123 lets the merchant override the
          // label; honour it exactly as the Liquid does.
          addButtonText.textContent =
            addButton.dataset.customLabel || vsString('addToCart', 'Add to cart');
        }
      }

      // Secondary / sticky button rendered under the SAME availability
      // condition by snippets/buy-buttons.liquid:169-171 and
      // sections/main-product.liquid:649-650.
      var sectionAtcBtn = document.getElementById('SectionAtcBtn-' + this.dataset.section);
      if (sectionAtcBtn) {
        if (disable) {
          sectionAtcBtn.setAttribute('disabled', 'disabled');
        } else {
          sectionAtcBtn.removeAttribute('disabled');
        }
      }
    }

    /** Dawn setUnavailable(), adapted to this theme's nodes. */
    setUnavailable() {
      var sectionId = this.dataset.section;

      var price = document.getElementById('price-' + sectionId);
      if (price) price.classList.add('hidden');

      var inventory = document.getElementById('Inventory-' + sectionId);
      if (inventory) inventory.classList.add('hidden');

      // sections/main-product.liquid:297 uses `visibility-hidden` for the SKU.
      var sku = document.getElementById('Sku-' + sectionId);
      if (sku) sku.classList.add('visibility-hidden');

      var inButtonPrice = document.getElementById('main-atc-price-' + sectionId);
      if (inButtonPrice) inButtonPrice.classList.add('hidden');

      var form = this.getProductForm();
      var addButtonText = form && form.querySelector('[name="add"] .main-atc__label__text');
      if (addButtonText) {
        addButtonText.textContent = vsString('unavailable', 'Unavailable');
      }

      vsPublishVariantChange(this, {
        sectionId: sectionId,
        originalSectionId: this.dataset.originalSection || sectionId,
        html: null,
        variant: null
      });
    }
  };

  if (!customElements.get('variant-selects')) {
    customElements.define('variant-selects', VariantSelects);
  }

  /* ================================================================== *
   * <card-variant-selects>  --  the collection-card swatch picker
   * ==================================================================
   *
   * vendor-specific, no Dawn upstream. Everything below is derived from
   * snippets/card-product.liquid:276-350 and its surroundings.
   *
   * Two facts shape the implementation:
   *   - The element has NO id: the Liquid writes `if='card-variant-selects-...'`
   *     (a typo for `id`) at snippets/card-product.liquid:277.
   *   - `data-section` (:278) is `product_form_id` = 'quick-add-' + section_id +
   *     card_product.id (:37), which is ALSO the <form>'s id (:421-427) and the
   *     `form=` target of every radio (:321, :342). So the form is
   *     document.getElementById(this.dataset.section) -- there is no
   *     `#product-form-<section>` here, and no Shopify section id at all,
   *     hence no `?section_id=` fetch.
   */

  var CardVariantSelects = class CardVariantSelects extends BaseElement {
    onVariantChange() {
      var self = this;

      vsGuard('card onVariantChange', function () {
        self.updateOptions();
        if (!self.hasCompleteSelection()) return;

        self.updateMasterId();
        if (!self.currentVariant) return;

        self.updateVariantInput();
        self.updateCardImage();
        self.updateCardButton();
      });

      // Deliberately NOT done here, because the markup does not support it:
      //  - no `.non-existent` / `.unavailable` recomputation: the element
      //    carries neither data-skip-non-existent nor data-skip-unavailable,
      //    so the base.css hiding rules can never fire inside a card;
      //  - no URL rewrite: data-update-url="false" is hard-coded (:281);
      //  - no setActiveMedia: a card has no <media-gallery>;
      //  - no `?section_id=` fetch: data-section is a form id, not a section id.
    }

    /**
     * snippets/card-product.liquid:429-435 -- the hidden input carries the same
     * `if=` typo, so it has no id either; find it through the form.
     * The `disabled` attribute is left alone on purpose: enabling it is
     * ProductForm's job in Dawn, i.e. the product-form cluster's.
     */
    updateVariantInput() {
      var form = document.getElementById(this.dataset.section);
      if (!form) return; // quick-add branch: the form is not in the DOM at all

      var input = form.querySelector('input[name="id"]');
      if (!input) return;

      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * snippets/card-product.liquid:98 -- id='{{ product_form_id }}-image',
     * the one per-card node that is uniquely addressable from data-section.
     * Widths mirror the srcset the Liquid renders at :81-87.
     */
    updateCardImage() {
      var image = this.currentVariant.featured_image;
      if (!image || !image.src) return;

      var img = document.getElementById(this.dataset.section + '-image');
      if (!img) return;

      var widths = [165, 360, 533, 720, 940, 1066];
      var srcset = [];

      for (var i = 0; i < widths.length; i++) {
        if (image.width >= widths[i]) {
          srcset.push(vsImageUrl(image.src, widths[i]) + ' ' + widths[i] + 'w');
        }
      }
      srcset.push(image.src + ' ' + image.width + 'w');

      img.setAttribute('srcset', srcset.join(','));
      img.setAttribute('src', vsImageUrl(image.src, 533));
      img.setAttribute('width', image.width);
      img.setAttribute('height', image.height);
      if (typeof image.alt === 'string') img.setAttribute('alt', image.alt);
    }

    /**
     * Direct add-to-cart branch only (snippets/card-product.liquid:436-458).
     * The quick-add branch renders a `<modal-opener>` button with the same id
     * (:375) but no sold-out markup, so we only touch what exists.
     */
    updateCardButton() {
      var button = document.getElementById(this.dataset.section + '-submit');
      if (!button) return;
      if (button.dataset.soldOutMessage !== 'true') return; // quick-add opener

      var label = button.querySelector('.main-atc__label__text'); // :449
      var soldOutMessage = button.querySelector('.sold-out-message'); // :456

      if (this.currentVariant.available) {
        button.removeAttribute('disabled');
        if (label) label.textContent = vsString('addToCart', 'Add to cart');
        if (soldOutMessage) soldOutMessage.classList.add('hidden');
      } else {
        button.setAttribute('disabled', 'disabled');
        if (label) label.textContent = vsString('soldOut', 'Sold out');
        if (soldOutMessage) soldOutMessage.classList.remove('hidden');
      }
    }
  };

  if (!customElements.get('card-variant-selects')) {
    customElements.define('card-variant-selects', CardVariantSelects);
  }
  /* ================================================================== *
   * <secondary-variant-select> / <secondary-variant-select-separate>
   *
   * The sticky add-to-cart bar's own variant picker, rendered at
   * sections/main-product.liquid:522 (open) and :628 (close). The tag name is
   * split by a Liquid conditional -- `picker_type == 'separate'` appends the
   * `-separate` suffix -- so BOTH names must be registered.
   *
   * Its markup is the same shape <variant-selects> consumes: `.product-form__input`
   * wrappers around `select.select__select`, plus an embedded
   * `<script type="application/json">` variant blob (:626). So the whole option
   * reading / variant matching / product-info rendering path is inherited
   * unchanged. Combined mode (:531-540) is the one shape <variant-selects> does
   * not otherwise see: a single select whose option `value` is the joined option
   * string, which getVariantData matching handles because updateOptions reads
   * `data-options` when present.
   * ================================================================== */

  var SecondaryVariantSelects = class SecondaryVariantSelects extends VariantSelects {};

  if (!customElements.get('secondary-variant-select')) {
    customElements.define('secondary-variant-select', SecondaryVariantSelects);
  }
  if (!customElements.get('secondary-variant-select-separate')) {
    customElements.define(
      'secondary-variant-select-separate',
      class SecondaryVariantSelectsSeparate extends SecondaryVariantSelects {}
    );
  }

  /* ------------------------------------------------------------------ *
   * Cross-picker sync + sticky-bar repaint.
   *
   * renderProductInfo() swaps a fixed list of nodes by id and none of them are
   * inside the sticky bar, so without this the sticky bar keeps the price,
   * image and sold-out state of the variant that was selected on page load.
   * Both pickers live in the same section, so the fetched document already
   * contains the correctly rendered sticky bar -- we copy from it rather than
   * formatting money in JS.
   * ------------------------------------------------------------------ */

  var vsSyncing = false;

  function vsSwap(html, sourceId, destId, opts) {
    opts = opts || {};
    var source = html.getElementById(sourceId);
    var dest = document.getElementById(destId);
    if (!source || !dest) return;
    dest.innerHTML = source.innerHTML;
    // Carry over the freshly rendered `hidden` state (main-product.liquid:656
    // adds it when the variant is unavailable).
    if (opts.syncHidden) {
      dest.classList.toggle('hidden', source.classList.contains('hidden'));
    }
  }

  /** Point every picker in the section at `variant` without re-entering onVariantChange. */
  function vsSyncSiblingPickers(sectionId, sourceEl, variant) {
    if (!variant || !variant.options) return;
    var pickers = document.querySelectorAll(
      'variant-selects[data-section="' + sectionId + '"],' +
      'secondary-variant-select[data-section="' + sectionId + '"],' +
      'secondary-variant-select-separate[data-section="' + sectionId + '"]'
    );

    Array.prototype.forEach.call(pickers, function (picker) {
      if (picker === sourceEl) return;

      // Combined mode: one select carrying the joined option string.
      var combined = picker.querySelector('select.sticky-atc__variant-select');
      if (combined) {
        combined.value = variant.options.join(',');
        return;
      }

      // Separate mode: one wrapper per option, in option order.
      var wrappers = picker.querySelectorAll('.product-form__input');
      Array.prototype.forEach.call(wrappers, function (wrapper, index) {
        var value = variant.options[index];
        if (value == null) return;

        var select = wrapper.querySelector('select.select__select');
        if (select) {
          select.value = value;
          return;
        }
        // Match by property rather than an attribute selector: option values are
        // merchant-authored and may contain quotes, which would break the selector.
        var radios = wrapper.querySelectorAll('input[type="radio"]');
        Array.prototype.forEach.call(radios, function (radio) {
          if (radio.value === value) radio.checked = true;
        });
      });
    });
  }

  function vsRepaintStickyBar(payload) {
    var sectionId = payload.sectionId;
    var sourceId = payload.originalSectionId || sectionId;
    var html = payload.html;
    var variant = payload.variant;
    if (!html || !variant) return;

    // Product image in the sticky bar (main-product.liquid:470).
    vsGuard('sticky image', function () {
      var source = html.getElementById('sticky-atc-image-' + sourceId);
      var dest = document.getElementById('sticky-atc-image-' + sectionId);
      if (source && dest && source.getAttribute('src')) {
        dest.setAttribute('src', source.getAttribute('src'));
        if (source.getAttribute('srcset')) dest.setAttribute('srcset', source.getAttribute('srcset'));
        dest.setAttribute('alt', source.getAttribute('alt') || '');
      } else if (!source && dest && variant.featured_image && variant.featured_image.src) {
        dest.setAttribute('src', vsImageUrl(variant.featured_image.src, 200));
      }
    });

    // Standalone price block (main-product.liquid:509).
    vsGuard('sticky separate price', function () {
      vsSwap(html, 'sticky-atc-separate-price-' + sourceId, 'sticky-atc-separate-price-' + sectionId);
    });

    // In-button price (main-product.liquid:656 and :684 -- duplicate id, one per layout).
    vsGuard('sticky button price', function () {
      vsSwap(html, 'sticky-atc-price-' + sourceId, 'sticky-atc-price-' + sectionId, { syncHidden: true });
    });

    // `.main-atc-price-span-<id>` is shared with snippets/buy-buttons.liquid:130 and :146,
    // so it must be updated BY CLASS -- there is more than one node per page.
    vsGuard('atc price spans', function () {
      var source = html.querySelector('.main-atc-price-span-' + sourceId);
      if (!source) return;
      var dests = document.querySelectorAll('.main-atc-price-span-' + sectionId);
      Array.prototype.forEach.call(dests, function (node) {
        node.innerHTML = source.innerHTML;
      });
    });

    // Sticky add-to-cart button state. `#SectionAtcBtn-<id>` is a duplicate id when
    // the secondary buy button is also enabled (buy-buttons.liquid:165), so select all.
    vsGuard('sticky atc button', function () {
      var buttons = document.querySelectorAll('[id="SectionAtcBtn-' + sectionId + '"]');
      Array.prototype.forEach.call(buttons, function (button) {
        var label = button.querySelector('.sticky-atc__label__text');
        if (variant.available) {
          button.removeAttribute('disabled');
          if (label && button.dataset.label) label.textContent = button.dataset.label;
        } else {
          button.setAttribute('disabled', 'disabled');
          if (label) label.textContent = vsString('soldOut', 'Sold out');
        }
      });
    });
  }

  vsGuard('sticky sync subscribe', function () {
    var eventName =
      (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variant-change';

    document.addEventListener(eventName, function (event) {
      if (vsSyncing) return;
      var payload = event.detail && event.detail.data;
      if (!payload || !payload.sectionId) return;

      vsSyncing = true;
      try {
        vsSyncSiblingPickers(payload.sectionId, event.target, payload.variant);
        vsRepaintStickyBar(payload);
      } finally {
        vsSyncing = false;
      }
    });
  });

})();
